import useTranslation from 'next-translate/useTranslation';

import { useAtom } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useDirectMessageWebSocket } from '@/components/pages/conversations/SocialInbox/hooks/useDirectMessageWebSocket';
import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import { useWindowFocus } from '@/components/pages/conversations/SocialInbox/hooks/useWindowFocus';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import type { DMConversation } from '@/components/pages/conversations/SocialInbox/types';

// Atoms
import { messageFilterSecondaryAtom } from '@/components/pages/conversations/SocialInbox/atoms';

// Components
import Icon from '@/components/base/Icon';
import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';
import { MessageSkeleton, SocialInboxMessage } from '@/components/pages/conversations/SocialInbox';
import { UnlockDMPermissionPopin } from '@/components/pages/conversations/SocialInbox/components/SocialInboxMessageList/DirectMessageList/UnlockDMPermissionPopin';

export const DirectMessageList = () => {
    const { t } = useTranslation('conversations');

    const [filterState] = useAtom(messageFilterSecondaryAtom);

    const { activeSocialProfile } = useActiveSocialProfile();
    const { messages, isLoadMore, isLoadingMore, loadMore, isLoading } = useSocialInboxDataSource();

    useDirectMessageWebSocket();
    useWindowFocus({
        onFocus: () => DirectMessageService.initiateConversationFetchJob(activeSocialProfile.id)
    });

    const filteredConversations = DirectMessageService.filterConversations(
        messages as DMConversation[],
        filterState
    );

    if (isLoading) {
        return (
            <div data-testid="message-skeleton-container" className="space-y-3">
                {[...Array(5).keys()].map((index) => (
                    <MessageSkeleton key={`message-skeleton-${index}`} />
                ))}
            </div>
        );
    }

    return (
        <div
            data-testid="message-list-container"
            className="grow space-y-2 overflow-y-scroll p-px pl-1 pr-4 pt-1">
            <UnlockDMPermissionPopin />
            {filteredConversations.length === 0 && (
                <div className="flex h-full flex-col items-center gap-4 pt-5">
                    <Icon icon="search" className="text-base" />
                    <div className="flex flex-col items-center gap-xxs">
                        <span className="font-GSemiBold text-sm">
                            {t(
                                `${
                                    filterState.search
                                        ? 'direct_messages.no_search_results'
                                        : 'direct_messages.no_results'
                                }`,
                                {
                                    search: filterState.search
                                }
                            )}
                        </span>
                        <span className="font-GMedium text-xs text-icoGray-500">
                            {t('direct_messages.no_search_results_description')}
                        </span>
                    </div>
                </div>
            )}

            {filteredConversations.map((message) => (
                <SocialInboxMessage message={message} key={message?.id} />
            ))}

            {isLoadMore ? (
                <LoadMoreInfinite loadMore={loadMore} isLoadingMore={isLoadingMore} />
            ) : (
                /* Spacer div to ensure bottom padding in scroll container */
                <div className="h-2 w-full" />
            )}
        </div>
    );
};
